import { Organizations } from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useTitle from "hooks/useTitle";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import {
  AdminListRequestOfSearchOrganizationUsersFilter,
  BulkEnrollmentRow,
  BulkUserEnrollmentRequest,
  GetSkusRequest,
  ListRequestOfRegistrationStateFilter,
  RegistrationStatus,
  ResolveUsersByEmailsRequest,
  ResponseCode,
  SearchOrganizationUsersFilter,
  SkuFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { convertToUTC } from "utils/dateUtils";
import { parsePastedEmails, parsePastedSkuCodes, PASTE_LIMIT } from "utils/parseDelimitedList";
import translateError from "utils/translateError";
import {
  BATCH_CAP,
  pairKey,
  ReviewRow,
  SEARCH_PAGE_SIZE,
  SelectedSku,
  SelectedUser,
  UserOption,
} from "./types";

const useBulkEnrollment = () => {
  const { formatMessage: t } = useIntl();
  const [searchParams] = useSearchParams();
  const { userSkuService, skuService, organizationUserService, usersUrl, userDetailsUrl } =
    useGlobalContext();
  const { organizationId } = contextStore.useState();
  const queryClient = useQueryClient();

  const title = t({ id: "bulkRegistration", defaultMessage: "Bulk Registration" });
  useTitle(title);

  const lockedUserId = searchParams.get("userId");
  const lockedSkuCode = searchParams.get("sku");
  const isUserLocked = !!lockedUserId;
  const isSkuLocked = !!lockedSkuCode;

  const lockedUserQuery = useQuery(
    ["bulk-reg-locked-user", lockedUserId],
    () => organizationUserService.getOrganizationUserDetails(lockedUserId!),
    { enabled: isUserLocked }
  );

  const lockedUser: UserOption | null = useMemo(() => {
    const found = lockedUserQuery.data;
    return found?.id && found?.organizationId
      ? {
          userId: found.id,
          organizationId: found.organizationId,
          email: found.email ?? "",
        }
      : null;
  }, [lockedUserQuery.data]);

  const lockedUserLoading = isUserLocked && lockedUserQuery.isLoading;
  const lockedUserLoadError = isUserLocked && lockedUserQuery.isError;

  const enrollmentOrganizationId = lockedUser?.organizationId ?? organizationId;
  const isRootOrganization = enrollmentOrganizationId === Organizations.KnowfullyLearningGroup;

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [crmOrderName, setCrmOrderName] = useState("");
  const [crmOrderNameError, setCrmOrderNameError] = useState<string>();
  const [isTest, setIsTest] = useState(false);
  const [bypassSkuExpiration, setBypassSkuExpiration] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [selectedSkus, setSelectedSkus] = useState<SelectedSku[]>([]);

  const [userQuery, setUserQuery] = useState("");
  const [skuQuery, setSkuQuery] = useState("");
  const debouncedUserQuery = useDebounce(userQuery, 350);
  const debouncedSkuQuery = useDebounce(skuQuery, 350);

  const [snackbar, setSnackbar] = useState<SnackbarMessageProps | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDialog, showDialogActions] = useBoolean(false);

  const toast = (message: string, severity: Severity = Severity.Error) =>
    setSnackbar(new SnackbarMessageProps(message, severity));

  function buildSkuCodesRequest(codes: string[]) {
    return GetSkusRequest.fromJS({
      organizationId: enrollmentOrganizationId,
      pageNumber: 0,
      pageSize: codes.length,
      parameters: SkuFilter.fromJS({ skuCodes: codes }),
    });
  }

  const buildUserSearchRequest = (searchText: string) =>
    AdminListRequestOfSearchOrganizationUsersFilter.fromJS({
      organizationId,
      pageNumber: 0,
      pageSize: SEARCH_PAGE_SIZE,
      parameters: SearchOrganizationUsersFilter.fromJS({
        searchText,
        mergeUserOrganizationId: organizationId,
      }),
    });

  // ── Locked-variant pre-resolution ──
  const lockedSkuQuery = useQuery(
    ["bulk-reg-locked-sku", lockedSkuCode, enrollmentOrganizationId],
    () => skuService.getSkus(buildSkuCodesRequest([lockedSkuCode ?? ""])),
    { enabled: isSkuLocked && !!enrollmentOrganizationId }
  );

  const lockedSku: SelectedSku | null = useMemo(() => {
    const match = lockedSkuQuery.data?.data?.find(
      (s) => s.sku?.toLowerCase() === lockedSkuCode?.toLowerCase()
    );
    return match?.id
      ? { skuId: match.id, code: match.sku ?? lockedSkuCode ?? "", title: match.title ?? "" }
      : null;
  }, [lockedSkuQuery.data, lockedSkuCode]);

  const effectiveUsers = useMemo(() => {
    if (!isUserLocked) {
      return selectedUsers;
    }
    return lockedUser ? [lockedUser] : [];
  }, [isUserLocked, lockedUser, selectedUsers]);
  const effectiveSkus = useMemo(() => {
    if (!isSkuLocked) {
      return selectedSkus;
    }
    return lockedSku ? [lockedSku] : [];
  }, [isSkuLocked, lockedSku, selectedSkus]);

  const wouldBreachCap = (extraUsers: number, extraSkus: number) =>
    Math.max(effectiveUsers.length + extraUsers, 1) * Math.max(effectiveSkus.length + extraSkus, 1) >
    BATCH_CAP;

  // ── User search ──
  const userSearch = useQuery(
    ["bulk-reg-user-search", debouncedUserQuery, organizationId],
    () => organizationUserService.searchOrganizationUsers(buildUserSearchRequest(debouncedUserQuery)),
    { enabled: !isUserLocked && debouncedUserQuery.length >= 2 && !!organizationId }
  );

  function buildSkuRequest(searchText: string) {
    return GetSkusRequest.fromJS({
      organizationId: enrollmentOrganizationId,
      pageNumber: 0,
      pageSize: SEARCH_PAGE_SIZE,
      parameters: SkuFilter.fromJS({ searchText }),
    });
  }

  const skuSearch = useQuery(
    ["bulk-reg-sku-search", debouncedSkuQuery, enrollmentOrganizationId],
    () => skuService.getSkus(buildSkuRequest(debouncedSkuQuery)),
    { enabled: !isSkuLocked && debouncedSkuQuery.length >= 2 && !!enrollmentOrganizationId }
  );

  const userOptions: UserOption[] = (userSearch.data?.data ?? [])
    .filter((u) => !selectedUsers.some((su) => su.userId === u.id))
    .map((u) => ({
      userId: u.id,
      organizationId: u.organizationId,
      email: u.email ?? "",
      organizationName: u.organizationName ?? undefined,
    }));
  const skuOptions: SelectedSku[] = (skuSearch.data?.data ?? [])
    .filter((s) => !selectedSkus.some((ss) => ss.skuId === s.id))
    .map((s) => ({
      skuId: s.id,
      code: s.sku ?? "",
      title: s.title ?? "",
    }));

  // ── Add ──
  const addUser = (user: SelectedUser) => {
    if (selectedUsers.some((u) => u.userId === user.userId)) {
      return;
    }
    if (wouldBreachCap(1, 0)) {
      toast(
        t(
          {
            id: "bulkRegistrationBatchCapReached",
            defaultMessage:
              "Batch cap reached: a single submit can contain at most {cap} user × SKU combinations.",
          },
          { cap: BATCH_CAP }
        )
      );
      return;
    }
    setSelectedUsers((prev) => [...prev, user]);
    setUserQuery("");
  };

  const addSku = (sku: SelectedSku) => {
    if (selectedSkus.some((s) => s.skuId === sku.skuId)) {
      return;
    }
    if (wouldBreachCap(0, 1)) {
      toast(
        t(
          {
            id: "bulkRegistrationBatchCapReached",
            defaultMessage:
              "Batch cap reached: a single submit can contain at most {cap} user × SKU combinations.",
          },
          { cap: BATCH_CAP }
        )
      );
      return;
    }
    setSelectedSkus((prev) => [...prev, sku]);
    setSkuQuery("");
  };

  // ── Paste ──
  const handleUserPaste = async (text: string) => {
    let emails = parsePastedEmails(text);
    if (emails.length === 0) {
      toast(t({ id: "bulkRegistrationNoEmails", defaultMessage: "No valid emails found in pasted text." }));
      return;
    }
    let overLimit = 0;
    if (emails.length > PASTE_LIMIT) {
      overLimit = emails.length - PASTE_LIMIT;
      emails = emails.slice(0, PASTE_LIMIT);
    }

    try {
      const response = await userSkuService.resolveUsersByEmails(
        ResolveUsersByEmailsRequest.fromJS({ organizationId, emails })
      );
      const resolved = response.data ?? [];

      let added = 0;
      let capSkipped = 0;
      let dupes = 0;
      const notFound: string[] = [];
      const next = [...selectedUsers];

      resolved.forEach((r) => {
        if (!r.found || !r.userId || !r.organizationId) {
          notFound.push(r.email ?? "");
          return;
        }
        if (next.some((u) => u.userId === r.userId)) {
          dupes++;
          return;
        }
        if ((next.length + 1) * Math.max(effectiveSkus.length, 1) > BATCH_CAP) {
          capSkipped++;
          return;
        }
        next.push({ userId: r.userId, organizationId: r.organizationId, email: r.email ?? "" });
        added++;
      });

      setSelectedUsers(next);
      setUserQuery("");
      notifyPasteOutcome(added, overLimit, capSkipped, dupes, notFound.filter(Boolean));
    } catch (error) {
      toast(translateError(error, t));
    }
  };

  const handleSkuPaste = async (text: string) => {
    let codes = parsePastedSkuCodes(text);
    if (codes.length === 0) {
      toast(t({ id: "bulkRegistrationNoSkus", defaultMessage: "No valid SKU codes found in pasted text." }));
      return;
    }
    let overLimit = 0;
    if (codes.length > PASTE_LIMIT) {
      overLimit = codes.length - PASTE_LIMIT;
      codes = codes.slice(0, PASTE_LIMIT);
    }

    let added = 0;
    let capSkipped = 0;
    let dupes = 0;
    const notFound: string[] = [];
    const next = [...selectedSkus];

    const matchByCode = new Map<string, SelectedSku>();
    try {
      const response = await skuService.getSkus(buildSkuCodesRequest(codes));
      for (const s of response.data ?? []) {
        if (s.id && s.sku) {
          matchByCode.set(s.sku.toLowerCase(), { skuId: s.id, code: s.sku, title: s.title ?? "" });
        }
      }
    } catch (error) {
      toast(translateError(error, t));
      return;
    }

    for (const code of codes) {
      const match = matchByCode.get(code.toLowerCase());
      if (!match) {
        notFound.push(code);
        continue;
      }
      if (next.some((s) => s.skuId === match.skuId)) {
        dupes++;
        continue;
      }
      if (Math.max(effectiveUsers.length, 1) * (next.length + 1) > BATCH_CAP) {
        capSkipped++;
        continue;
      }
      next.push(match);
      added++;
    }

    setSelectedSkus(next);
    setSkuQuery("");
    notifyPasteOutcome(added, overLimit, capSkipped, dupes, notFound);
  };

  const notifyPasteOutcome = (
    added: number,
    overLimit: number,
    capSkipped: number,
    dupes: number,
    notFound: string[]
  ) => {
    const parts: string[] = [];
    if (added > 0) {
      parts.push(t({ id: "bulkRegistrationAdded", defaultMessage: "{count} added." }, { count: added }));
    }
    if (dupes > 0) {
      parts.push(
        t({ id: "bulkRegistrationDuplicates", defaultMessage: "{count} duplicates skipped." }, { count: dupes })
      );
    }
    if (overLimit > 0) {
      parts.push(
        t(
          { id: "bulkRegistrationPasteLimit", defaultMessage: "{extra} ignored over the {limit}-per-paste limit." },
          { limit: PASTE_LIMIT, extra: overLimit }
        )
      );
    }
    if (capSkipped > 0) {
      parts.push(
        t(
          { id: "bulkRegistrationCapSkipped", defaultMessage: "{count} skipped (max {cap} combinations)." },
          { count: capSkipped, cap: BATCH_CAP }
        )
      );
    }
    if (notFound.length > 0) {
      const preview = notFound.slice(0, 5).join(", ");
      parts.push(
        notFound.length > 5
          ? t(
              { id: "bulkRegistrationNotFoundMany", defaultMessage: "{count} not found: {preview}, …" },
              { count: notFound.length, preview }
            )
          : t({ id: "bulkRegistrationNotFound", defaultMessage: "Not found: {preview}" }, { preview })
      );
    }
    if (parts.length === 0) {
      return;
    }
    const hadIssues = overLimit > 0 || capSkipped > 0 || dupes > 0 || notFound.length > 0;
    toast(parts.join(" "), hadIssues ? Severity.Warning : Severity.Success);
  };

  // ── Review table (cross-product) + per-pair status ──
  const rows: ReviewRow[] = useMemo(() => {
    const result: ReviewRow[] = [];
    effectiveUsers.forEach((user) => {
      effectiveSkus.forEach((sku) => {
        result.push({ ...user, ...sku });
      });
    });
    return result;
  }, [effectiveUsers, effectiveSkus]);

  const rowsKey = rows
    .map((r) => pairKey(r.userId, r.skuId))
    .sort((a, b) => a.localeCompare(b))
    .join(",");

  const statusQuery = useQuery(
    ["bulk-reg-status", organizationId, rowsKey],
    () =>
      userSkuService.getRegistrationStatuses(
        ListRequestOfRegistrationStateFilter.fromJS({
          pageSize: -1,
          parameters: {
            pairs: rows.map((r) => ({
              userId: r.userId,
              organizationId: r.organizationId,
              skuId: r.skuId,
            })),
          },
        })
      ),
    { enabled: rows.length > 0 }
  );

  const statusByPair = useMemo(() => {
    const map: Record<string, RegistrationStatus> = {};
    (statusQuery.data?.data ?? []).forEach((s) => {
      if (s.userId && s.skuId) {
        map[pairKey(s.userId, s.skuId)] = s;
      }
    });
    return map;
  }, [statusQuery.data]);

  const newCount = Object.values(statusByPair).filter((s) => !s.isExisting).length;
  const extendCount = Object.values(statusByPair).filter((s) => s.isExisting).length;
  const hasUnresolvedStatus = rows.length > 0 && statusQuery.isFetching;
  const hasLockedActiveRegistration =
    isUserLocked && Object.values(statusByPair).some((s) => s.isExisting);

  // ── Submit ──
  // The endpoint validates the CRM Order Name against CRM and answers BusinessRuleFailed when the
  // order does not exist, so on this request that code always belongs to the CRM Order Name field.
  const isCrmOrderNameRejection = (error: any) =>
    !!crmOrderName && error?.data?.responseCode === ResponseCode.BusinessRuleFailed;

  const submit = async () => {
    try {
      setSubmitting(true);
      setCrmOrderNameError(undefined);
      const request = BulkUserEnrollmentRequest.fromJS({
        rows: rows.map((r) =>
          BulkEnrollmentRow.fromJS({
            userId: r.userId,
            organizationId: r.organizationId,
            skuId: r.skuId,
          })
        ),
        endDate: endDate ? convertToUTC(endDate) : undefined,
        crmOrderName: crmOrderName || undefined,
        bypassSkuExpiration,
        isTest,
      });
      const response = await userSkuService.addBulkEnrollment(request);
      if (response.responseCode !== ResponseCode.OK) {
        toast(response.responseReason ?? t({ id: "somethingWentWrong", defaultMessage: "Something went wrong." }));
        return;
      }
      Array.from(new Set(rows.map((r) => r.userId))).forEach((userId) =>
        queryClient.invalidateQueries(["userSkus", userId])
      );
      showDialogActions.setTrue();
    } catch (error) {
      const message = translateError(error, t);
      toast(message);
      if (isCrmOrderNameRejection(error)) {
        setCrmOrderNameError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const detailsDisabled = isRootOrganization;
  const lockedUserUnresolved = isUserLocked && lockedUserQuery.isFetched && !lockedUser;
  const backToLockedUser = isUserLocked && !lockedUserUnresolved;
  const backUrl = backToLockedUser && lockedUserId ? userDetailsUrl(lockedUserId) : usersUrl();
  const backLabelKey = backToLockedUser ? "backToUser" : "backToUsers";

  let pageTitle = title;
  if (isUserLocked) {
    pageTitle = lockedUser
      ? t(
          { id: "bulkRegistrationForUser", defaultMessage: "Add Enrollment for {user}" },
          { user: lockedUser.email }
        )
      : title;
  } else if (isSkuLocked) {
    pageTitle = t(
      { id: "bulkRegistrationForSku", defaultMessage: "Add Users to {sku}" },
      { sku: lockedSkuCode ?? "" }
    );
  }

  return {
    usersUrl,
    pageTitle,
    isRootOrganization,
    detailsDisabled,
    isUserLocked,
    isSkuLocked,
    lockedSkuCode,
    lockedUserLoading,
    lockedUserLoadError,
    backUrl,
    backLabelKey,
    hasUnresolvedStatus,
    hasLockedActiveRegistration,
    endDate,
    setEndDate,
    crmOrderName,
    setCrmOrderName: (value: string) => {
      setCrmOrderNameError(undefined);
      setCrmOrderName(value);
    },
    crmOrderNameError,
    isTest,
    setIsTest,
    bypassSkuExpiration,
    setBypassSkuExpiration,
    effectiveUsers,
    setSelectedUsers,
    effectiveSkus,
    setSelectedSkus,
    userQuery,
    skuQuery,
    setUserQuery,
    setSkuQuery,
    userOptions,
    skuOptions,
    addUser,
    addSku,
    handleUserPaste,
    handleSkuPaste,
    rows,
    statusByPair,
    statusFetching: statusQuery.isFetching,
    newCount,
    extendCount,
    submit,
    submitting,
    showDialog,
    showDialogActions,
    snackbar,
    setSnackbar,
  };
};

export default useBulkEnrollment;
